import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { AuthStatus, Locale } from '@dashboard/contracts';
import { App } from './App';
import { initI18n } from './i18n';
import { apiRequest } from './api/client';
import { defaultQueryOptions, queryKeys } from './api/queries';
import './styles.css';
/* Feature stylesheet, imported after the base one so its tokens are defined.
   See the comment at the top of the file for why it is not in `styles.css`. */
import './styles.meetings.css';
import './styles.capture.css';
import './styles.structure.css';
import './styles.fleet.css';

/**
 * One shared query client. Its defaults encode the loading policy from section
 * 15.2: cached data is reused rather than refetched on every mount, requests
 * carry an abort signal so navigating away cancels them, and authentication and
 * permission failures are never retried.
 */
const queryClient = new QueryClient({
  defaultOptions: { queries: defaultQueryOptions },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element missing');

/**
 * Resolves the signed-in person's own locale, if any, before the app ever
 * mounts.
 *
 * The alternative -- boot in a default locale and switch once the session is
 * known -- was tried first, and it does not just flash briefly: `App.tsx`
 * synchronously mounts the header shell and *then*, in a `useEffect`, applies
 * the real locale once `/api/v1/auth/status` resolves. Everything that mounts
 * after that point (every routed page, behind `Suspense`) only ever sees the
 * corrected language and is fine; the shell -- rendered once, in the window
 * before the switch -- depends on that one effect actually reaching every
 * subscriber in time, and a user whose language an administrator changed
 * before their first sign-in reported exactly that failure mode: pages in
 * Finnish, the header stuck in English. Resolving the locale first removes
 * the window entirely rather than trying to win the race inside it.
 *
 * This issues the exact request `useAuthStatus` would otherwise make first,
 * against the same query key, so priming the cache here means the in-app
 * query is answered from cache rather than firing a second request.
 */
async function resolveInitialLocale(): Promise<Locale> {
  try {
    const status = await queryClient.fetchQuery({
      queryKey: queryKeys.authStatus,
      queryFn: () => apiRequest<AuthStatus>('/api/v1/auth/status'),
      // Best-effort: an unreachable API should not delay the shell behind
      // retries and backoff. `useAuthStatus` still applies the normal policy
      // (and shows its own retry UI) once it takes over, because a failure
      // here leaves the query key uncached rather than poisoned.
      retry: false,
    });
    return status.authenticated ? status.context.user.locale : 'en';
  } catch {
    return 'en';
  }
}

/** Session flag, so a browser that refuses to unregister cannot reload forever. */
const DEV_WORKER_CLEARED_KEY = 'dashboard.devServiceWorkerCleared';

/**
 * Removes a service worker left behind by an earlier development session.
 *
 * Nothing registers one against the dev server any more, but a browser that
 * already did keeps both the worker and its cache until something says
 * otherwise, and that cache is the whole problem. `public/service-worker.js`
 * serves everything but navigations cache-first, which it justifies --
 * correctly, for a build -- with "a changed file is a different URL, so a
 * cached one can never be stale". The dev server does not work that way: its
 * module URLs are stable paths (`/src/theme-provider.tsx`), while the
 * pre-bundled dependencies beside them carry a hash that changes every time
 * Vite re-optimizes (`.vite/deps/react.js?v=...`).
 *
 * So a cached module graph keeps importing the React it was compiled against
 * while the current one resolves a different URL, the page ends up with two
 * copies of React, and the first hook to run throws `Invalid hook call` --
 * which surfaced as a white screen and `Cannot read properties of null
 * (reading 'useState')` in `ThemeProvider`, the first component to call one.
 *
 * A hard reload appeared to fix it because Ctrl-F5 bypasses the worker
 * entirely; it never touched the stale cache, so the next ordinary visit broke
 * again. That is also why this is worth doing rather than leaving to a manual
 * "unregister" in devtools: the symptom does not point at a service worker,
 * and the one gesture that makes it go away is the one that hides the cause.
 */
async function removeDevServiceWorker(): Promise<void> {
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));

  /* The worker is gone; its cache is not, and a later production build served
     from this origin would inherit it. */
  if (typeof caches !== 'undefined') {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }

  /*
   * This page's imports were resolved before any of this ran, so unregistering
   * cannot rescue the load in progress -- one reload can, and only one is ever
   * needed, because an uncontrolled page never reaches this branch again.
   */
  if (navigator.serviceWorker.controller && !sessionStorage.getItem(DEV_WORKER_CLEARED_KEY)) {
    sessionStorage.setItem(DEV_WORKER_CLEARED_KEY, '1');
    window.location.reload();
  }
}

void resolveInitialLocale().then((locale) =>
  initI18n(locale).then(() => {
    /*
     * The offline shell, registered after the application has started so it
     * never competes with the first paint.
     *
     * Its only job is that `/capture` loads on a phone with no signal; every
     * API read still goes to the network and still fails visibly when there is
     * none, because a stale schedule served from a cache is worse than no
     * schedule (see public/service-worker.js).
     */
    if ('serviceWorker' in navigator) {
      /*
       * Production builds only.
       *
       * The worker's runtime branch is cache-first "because the names are
       * hashed", which is true of the Rollup output and false of the dev
       * server -- so registering it there is what `removeDevServiceWorker`
       * exists to undo. `e2e/base-url.ts` already records the same asymmetry
       * as the reason the offline specs skip against 5173; the registration
       * had simply never been told about it.
       */
      if (!import.meta.env.PROD) {
        void removeDevServiceWorker().catch(() => {
          /* Nothing to clean up, or a browser that would not: either way
             development is fine now that nothing registers a worker. */
        });
      } else {
        const register = () =>
          void navigator.serviceWorker.register('/service-worker.js').catch(() => {
            /* An unsupported or refused registration costs the offline shell and
               nothing else: the application works exactly as it did before. */
          });

        /*
         * `load` has usually already fired by the time this runs: the locale is
         * resolved with an awaited request, and a listener attached after the
         * event never hears it. Registering immediately in that case is what the
         * first version got wrong -- the worker was never installed at all, and
         * nothing said so, because a listener that is never called looks exactly
         * like one that succeeded.
         */
        if (document.readyState === 'complete') register();
        else window.addEventListener('load', register, { once: true });
      }
    }

    createRoot(container).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </StrictMode>,
    );
  }),
);
